import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ScanSearch, Loader2, FlaskConical, Clock, AlertTriangle, ShieldCheck, ShieldAlert, GitCompareArrows, Lock, FileSpreadsheet, ClipboardCheck } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import useAuth from "../../hooks/useAuth";
import { authApi } from "../../lib/auth";
import { errorMessage } from "../../lib/api";
import ScanOverview from "../../components/workspace/ScanOverview";
import SkuTable from "../../components/workspace/SkuTable";
import ScanUsersTable from "../../components/workspace/ScanUsersTable";
import CaPolicies from "../../components/workspace/CaPolicies";
import LicenseGaps from "../../components/workspace/LicenseGaps";
import ScanCompare from "../../components/workspace/ScanCompare";
import ConnectionPanel from "../../components/workspace/ConnectionPanel";
import SecurityPosture from "../../components/workspace/SecurityPosture";
import SchedulePicker from "../../components/workspace/SchedulePicker";
import Planner from "../../components/workspace/Planner";
import ImportCsvDialog from "../../components/workspace/ImportCsvDialog";
import ComplianceDashboard from "../../components/compliance/ComplianceDashboard";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "");

export default function TenantDetailPage() {
  const { id } = useParams();
  const { isAdmin } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [scans, setScans] = useState([]);
  const [scanId, setScanId] = useState("");
  const [scan, setScan] = useState(null);
  const [starting, setStarting] = useState(false);
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "overview");
  const [usersFilter, setUsersFilter] = useState("all");
  const [plannerPrefill, setPlannerPrefill] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const isCsv = tenant?.source === "csv";
  const compareInit = useRef({ from: params.get("from") || "", to: params.get("to") || "" });
  const announced = useRef(new Set());
  const timer = useRef(null);

  const loadTenant = useCallback(() => authApi.tenants.get(id).then(setTenant).catch((err) => toast.error("Tenant not found", { description: errorMessage(err) })), [id]);
  const loadScans = useCallback(async () => {
    const list = await authApi.tenants.scans(id);
    setScans(list);
    return list;
  }, [id]);

  useEffect(() => {
    loadTenant();
    loadScans().then((list) => {
      const wanted = compareInit.current.to && list.find((s) => s.id === compareInit.current.to && s.status === "done");
      const latest = wanted || list.find((s) => s.status === "done") || list[0];
      if (latest) setScanId(latest.id);
    });
  }, [id, loadTenant, loadScans]);

  const changeTab = (value) => {
    setTab(value);
    if (params.toString()) setParams({}, { replace: true });
  };

  useEffect(() => {
    if (!scanId) return undefined;
    let canceled = false;
    const poll = async () => {
      try {
        const s = await authApi.scans.get(scanId);
        if (canceled) return;
        setScan(s);
        if (s.status === "running") {
          announced.current.add(s.id);
          timer.current = setTimeout(poll, 2000);
        } else {
          loadScans();
          loadTenant();
          if (s.status === "error") toast.error("Scan failed", { description: s.error });
          else if (s.auto && s.changes && announced.current.has(s.id)) {
            announced.current.delete(s.id);
            if (s.changes.has_changes) toast.warning(`${s.changes.lines.length} change${s.changes.lines.length === 1 ? "" : "s"} since the previous scan`, { description: s.changes.lines.slice(0, 3).join(" · ") });
            else if (!s.changes.first) toast.success("No changes since the previous scan");
          }
        }
      } catch (err) {
        if (!canceled) toast.error("Could not load scan", { description: errorMessage(err) });
      }
    };
    poll();
    return () => {
      canceled = true;
      clearTimeout(timer.current);
    };
  }, [scanId, loadScans, loadTenant]);

  const startScan = async () => {
    setStarting(true);
    try {
      const res = await authApi.tenants.scan(id);
      toast.success(res.already_running ? "A scan is already running" : "Scan started");
      setScan(null);
      setScanId(res.id);
      loadScans();
    } catch (err) {
      toast.error("Could not start scan", { description: errorMessage(err) });
    } finally {
      setStarting(false);
    }
  };

  const showUsers = (filter) => {
    setUsersFilter(filter);
    setTab("users");
  };
  const planFix = (moves, label) => {
    setPlannerPrefill({ moves, label, ts: Date.now() });
    setTab("planner");
  };
  const onScheduledScan = (scanId2) => {
    loadScans();
    loadTenant();
    if (scanId2) setScanId(scanId2);
  };

  const summary = scan?.status === "done" ? scan.summary : null;
  const running = scan?.status === "running";
  const gapCount = summary?.gaps?.users_with_gaps;
  const secScore = summary?.security?.score;

  return (
    <div className="ws-page" data-testid="tenant-detail">
      <Link to="/app/tenants" className="ws-back" data-testid="back-to-tenants">
        <ArrowLeft size={14} /> All tenants
      </Link>
      <header className="ws-head">
        <div>
          <h1 data-testid="tenant-title">
            {tenant?.sample && <FlaskConical size={18} className="sample-ico" />}
            {tenant?.name || "..."}
          </h1>
          <p>
            {tenant?.org_display_name ? `${tenant.org_display_name} · ${tenant.default_domain}` : tenant?.sample ? "Sample data - no Microsoft connection" : tenant?.tenant_id}
            {isCsv && <span className="mini-chip" style={{ marginLeft: 8 }}>CSV import</span>}
            {tenant?.status === "error" && (
              <span className="ws-inline-alert">
                <AlertTriangle size={12} /> {tenant.last_error}
              </span>
            )}
          </p>
        </div>
        <div className="ws-head-actions">
          {isAdmin && tenant && !isCsv && <SchedulePicker tenant={tenant} onChange={setTenant} onScanStarted={onScheduledScan} />}
          {scans.length > 0 && (
            <label className="ws-select">
              <Clock size={13} />
              <select value={scanId} onChange={(e) => setScanId(e.target.value)} data-testid="scan-select">
                {scans.map((s) => (
                  <option key={s.id} value={s.id}>
                    {`${fmt(s.started_at)} · ${s.status}${s.user_count ? ` · ${s.user_count} users` : ""}${s.auto ? " · auto" : ""}${s.source === "csv" ? " · csv" : ""}`}
                  </option>
                ))}
              </select>
            </label>
          )}
          {isAdmin && tenant && (
            <button type="button" className={`tb-btn ${isCsv ? "feature cost" : ""}`} onClick={() => setImportOpen(true)} data-testid="import-csv-btn" title="Import a Microsoft 365 admin center export as a new scan">
              <span className="ico">
                <FileSpreadsheet size={14} />
              </span>
              Import CSV
            </button>
          )}
          {isAdmin && !isCsv && (
            <button type="button" className="tb-btn feature cost" onClick={startScan} disabled={starting || running} data-testid="run-scan-btn">
              <span className="ico">{starting || running ? <Loader2 size={14} className="spin" /> : <ScanSearch size={14} />}</span>
              {running ? "Scanning..." : "Run scan"}
            </button>
          )}
        </div>
      </header>

      {tenant && !tenant.sample && !isCsv && <ConnectionPanel tenant={tenant} isAdmin={isAdmin} onChange={loadTenant} />}

      {!scan && scans.length === 0 && (
        <div className="ws-empty glass" data-testid="no-scans">
          <ScanSearch size={28} />
          <h3>{isCsv ? "No import yet" : "No scans yet"}</h3>
          <p>{isCsv ? "Import a Microsoft 365 admin center export to populate this tenant." : isAdmin ? "Run the first scan to pull licenses, users and Conditional Access policies." : "An admin needs to run the first scan."}</p>
        </div>
      )}
      {running && (
        <div className="ws-empty glass" data-testid="scan-running">
          <Loader2 size={26} className="spin" />
          <h3>Scanning tenant...</h3>
          <p>Reading organization, subscribed SKUs, users, groups and Conditional Access policies from Microsoft Graph.</p>
        </div>
      )}
      {scan?.status === "error" && (
        <div className="ws-alert warn big" data-testid="scan-error">
          <AlertTriangle size={16} /> {scan.error}
        </div>
      )}

      {summary && (
        <Tabs value={tab} onValueChange={changeTab} className="ws-tabs">
          <TabsList className="ws-tablist">
            <TabsTrigger value="overview" data-testid="tab-overview">
              Overview
            </TabsTrigger>
            <TabsTrigger value="licenses" data-testid="tab-licenses">
              Licenses <span className="count">{summary.skus.filter((s) => s.consumed > 0).length}</span>
            </TabsTrigger>
            <TabsTrigger value="users" data-testid="tab-users">
              Users <span className="count">{summary.users.total}</span>
            </TabsTrigger>
            <TabsTrigger value="ca" data-testid="tab-ca">
              <ShieldCheck size={13} /> Conditional Access {summary.ca ? <span className="count">{summary.ca.total}</span> : null}
            </TabsTrigger>
            <TabsTrigger value="gaps" data-testid="tab-gaps">
              <ShieldAlert size={13} /> License gaps {gapCount ? <span className="count warn">{gapCount}</span> : null}
            </TabsTrigger>
            <TabsTrigger value="security" data-testid="tab-security">
              <Lock size={13} /> Security {secScore && (secScore.crit || secScore.warn) ? <span className={`count ${secScore.crit ? "crit" : "warn"}`}>{secScore.crit + secScore.warn}</span> : null}
            </TabsTrigger>
            <TabsTrigger value="compare" data-testid="tab-compare">
              <GitCompareArrows size={13} /> Compare
            </TabsTrigger>
            <TabsTrigger value="cis" data-testid="tab-cis">
              <ClipboardCheck size={13} /> CIS
            </TabsTrigger>
            <TabsTrigger value="planner" data-testid="tab-planner">
              Target-state planner
            </TabsTrigger>
          </TabsList>
          <TabsContent value="overview">
            <ScanOverview
              scan={scan}
              onCompare={(from, to) => {
                compareInit.current = { from, to };
                setTab("compare");
              }}
            />
          </TabsContent>
          <TabsContent value="licenses">
            <SkuTable skus={summary.skus} />
          </TabsContent>
          <TabsContent value="users">
            <ScanUsersTable key={`${scan.id}-${usersFilter}`} scanId={scan.id} summary={summary} initialFilter={usersFilter} />
          </TabsContent>
          <TabsContent value="ca">
            <CaPolicies ca={summary.ca} error={scan.summary.ca_error} source={summary.source} onShowGaps={() => setTab("gaps")} />
          </TabsContent>
          <TabsContent value="gaps">
            <LicenseGaps summary={summary} scanId={scan.id} onShowUsers={() => showUsers("gaps")} onPlanFix={planFix} isAdmin={isAdmin} />
          </TabsContent>
          <TabsContent value="security">
            <SecurityPosture summary={summary} scanId={scan.id} onShowUsers={() => showUsers("nomfa")} onShowCa={() => setTab("ca")} onPlanFix={planFix} isAdmin={isAdmin} />
          </TabsContent>
          <TabsContent value="compare">
            <ScanCompare tenantId={id} scans={scans} currentScanId={scan.id} initialFrom={compareInit.current.from} initialTo={compareInit.current.to} />
          </TabsContent>
          <TabsContent value="cis">
            <ComplianceDashboard tenant={tenant} isAdmin={isAdmin} />
          </TabsContent>
          <TabsContent value="planner">
            <Planner tenant={tenant} scan={scan} isAdmin={isAdmin} prefill={plannerPrefill} />
          </TabsContent>
        </Tabs>
      )}
      {importOpen && tenant && (
        <ImportCsvDialog
          open
          onOpenChange={setImportOpen}
          tenant={tenant}
          onDone={(res) => {
            loadTenant();
            loadScans().then(() => setScanId(res.scan_id));
          }}
        />
      )}
    </div>
  );
}
